import { isAfter, isBefore, subMinutes } from 'date-fns';
import { buildASTSchema, graphql, GraphQLError, type DocumentNode } from 'graphql';
import type {
  AlertType,
  Box,
  Maybe,
  Mutation,
  MutationAcceptPrivacyPolicyArgs,
  MutationUnlockBoxArgs,
  MutationUpdateMeArgs,
  Query,
  QueryShipmentByIdArgs,
  QueryShipmentsForConsigneeArgs,
  Shipment,
  ShipmentTypeFilter,
  User,
} from 'graphql-models';
import { delay, http, HttpResponse, graphql as mswGraphql, passthrough } from 'msw';
import { setupWorker } from 'msw/browser';
import config from 'walden-trace-app/config/environment';
import { alertFactory } from './mocks/alert';
import { temperatureMeasurementFactory } from './mocks/measurements';
import { shipmentFactory, temperatureSeries } from './mocks/shipment';
import { userFactory } from './mocks/user';
import { slicedPaginatedData } from './mocks/paginator-info';

export default {
  start: async (schema: DocumentNode) => {
    const worker = setupWorker();

    const graphqlOperation = createGraphqlOperationHandler(schema);
    worker.use(graphqlOperation);

    worker.use(http.get('/assets/*', passthrough));
    worker.use(http.post('https://*.mapbox.com/*', passthrough));
    worker.use(http.get('https://*.mapbox.com/*', passthrough));
    worker.use(http.get(`${config.msal.authority}/*`, passthrough));
    worker.use(http.post(`${config.msal.authority}/*`, passthrough));

    await worker.start();

    return worker;
  },
  graphql: mswGraphql,
};

// blatantly stole this from @bagaar/ember-graphql-mocking , but I couldn't import it, maybe this is only possible in test code?
function createGraphqlOperationHandler(schemaDocument: DocumentNode) {
  const schema = buildASTSchema(schemaDocument);
  const graphqlOperation = mswGraphql.operation(async ({ query, variables }) => {
    const { data, errors } = await graphql({
      rootValue: root,
      schema,
      source: query,
      variableValues: variables,
    });

    // @ts-expect-error: dont think this is something we can / should type
    return HttpResponse.json({ errors, data });
  });

  return graphqlOperation;
}

type QueryMethods =
  | Query
  | { shipmentById: (args: QueryShipmentByIdArgs) => Query['shipmentById'] }
  | {
      shipmentsForConsignee: (
        args: QueryShipmentsForConsigneeArgs,
      ) => Promise<Query['shipmentsForConsignee']>;
    };

type MutationMethods =
  | Mutation
  | { unlockBox: (args: MutationUnlockBoxArgs) => Promise<Maybe<Box>> }
  | { updateMe: (args: MutationUpdateMeArgs) => Promise<Maybe<User>> }
  | { acceptPrivacyPolicy: (args: MutationAcceptPrivacyPolicyArgs) => Promise<Maybe<User>> };

const now = new Date();

const DELIVERED_SHIPMENTS = [
  shipmentFactory({
    deliveredAt: new Date('2025-02-21T14:15:35.000Z').toISOString(),
    clientName: 'ACME Corp',
    updatedAt: new Date('Mon Feb 21 2025 15:15:35 GMT+0100').toISOString(),
    id: '111',
    state: 'DELIVERED',
    parcelId: 'SHIP-DEL-03240018',
    parcelSequenceNumber: 1,
    parcelTotal: 3,
  }),
  shipmentFactory({
    deliveredAt: new Date('2025-02-21T13:15:35.000Z').toISOString(),
    clientName: 'Moderna',
    updatedAt: new Date('Mon Feb 21 2025 15:15:35 GMT+0100').toISOString(),
    endedAt: new Date('Mon Feb 21 2025 15:15:35 GMT+0100').toISOString(),
    id: '112',
    state: 'DELIVERED',
    parcelId: 'SHIP-DEL-03243303',
    parcelSequenceNumber: 1,
    parcelTotal: 3,
    externalUuid: '01992de6-db05-730e-a448-0f6c266ae162',
  }).forBox({
    id: '111',
    desiredLocked: true,
  }),
  shipmentFactory({
    deliveredAt: new Date('2025-02-21T15:15:35.000Z').toISOString(),
    clientName: 'Moderna',
    id: '113',
    state: 'DELIVERED',
    parcelId: 'SHIP-DEL-03240976',
    parcelSequenceNumber: 2,
    parcelTotal: 3,
  }).forBox({
    id: '222',
    desiredLocked: false,
  }),
  shipmentFactory({ state: 'DELIVERED' }),
  shipmentFactory({ state: 'DELIVERED' }),
  shipmentFactory({ state: 'DELIVERED' }),
  shipmentFactory({ state: 'DELIVERED' }),
  shipmentFactory({ state: 'DELIVERED' }),
  shipmentFactory({ state: 'DELIVERED' }),
];

const ARRIVING_SHIPMENTS = [
  shipmentFactory({
    clientName: 'Pfizer',
    expectedDeliveryDate: new Date('2025-11-24T14:15:35.000Z').toISOString(),
    id: '222',
    parcelId: 'SHIP-ARR-04088923',
    state: 'TRANSPORT_ORDER_RECEIVED',
    parcelSequenceNumber: 1,
    parcelTotal: 2,
  }).forBox({
    id: '333',
  }),
  shipmentFactory({
    clientName: 'Pfizer',
    expectedDeliveryDate: new Date('2025-03-21T14:15:35.000Z').toISOString(),
    id: '223',
    state: 'TRANSPORT_ORDER_RECEIVED',
    parcelId: 'SHIP-ARR-04082091',
    parcelSequenceNumber: 1,
    parcelTotal: 1,
  }),
  shipmentFactory({
    clientName: 'Pfizer',
    id: '224',
    state: 'ORDER_PACKED',
    parcelId: 'SHIP-ARR-04081027',
    parcelSequenceNumber: 3,
    parcelTotal: 3,
    minTemperature: 4.5,
    maxTemperature: 9.8,
    currentTemperature: temperatureMeasurementFactory({
      valueCelsius: 9.8,
      measuredAt: new Date('Mon Mar 21 2025 15:11:35 GMT+0100').toISOString(),
    }),
    temperatureMeasurements: temperatureSeries(subMinutes(now, 20), 5, 2, 8, 9.8),
    alerts: [
      alertFactory({
        type: 'TEMPERATURE_EXCEEDED' as AlertType,
      }),
      alertFactory({
        id: '2',
        type: 'SHOCK_DETECTED' as AlertType,
      }),
    ],
  }).forBox({
    id: '444',
  }),
];

const PLANNED_SHIPMENTS = [
  shipmentFactory({
    id: '331',
    state: 'SHIPPING_IN_PROGRESS',
    parcelId: 'SHIP-PLN-04230054',
  }),
  shipmentFactory({
    id: '332',
    state: 'ORDER_RECEIVED',
    parcelId: 'SHIP-PLN-04239089',
  }).forBox({
    id: '555',
  }),
];

const RECENT_SHIPMENTS = [
  shipmentFactory({
    id: '1',
    expectedDeliveryDate: new Date('2025-03-21T14:15:35.000Z').toISOString(),

    parcelId: 'SHIP-OTH-12150876',
    minTemperature: 5.1,
    maxTemperature: 6.8,
    currentTemperature: temperatureMeasurementFactory({
      valueCelsius: 6.8,
    }),
    temperatureMeasurements: temperatureSeries(subMinutes(now, 20)),
  }).forBox({ desiredLocked: true, id: 'unlocked-box-1' }),
  shipmentFactory({
    id: '442',
    expectedDeliveryDate: new Date('2025-03-21T14:15:35.000Z').toISOString(),

    state: 'SHIPPING_IN_PROGRESS',
    parcelId: 'SHIP-OTH-12118913',
    alerts: [alertFactory({})],
    temperatureMeasurements: temperatureSeries(subMinutes(now, 20), 5, 2, 8, 1.2),
  }).forBox({ desiredLocked: false, id: 'locked-box-1' }),
  shipmentFactory({
    id: '443',
    expectedDeliveryDate: new Date('2025-03-21T14:15:35.000Z').toISOString(),

    parcelId: 'SHIP-OTH-12091212',
  }).forBox({
    id: 'unlocked-box-2',
    desiredLocked: false,
  }),
  shipmentFactory({
    id: '444',
    parcelId: 'SHIP-OTH-11304423',
  }),
];

const SHIPMENTS = [
  ...DELIVERED_SHIPMENTS,
  ...ARRIVING_SHIPMENTS,
  ...PLANNED_SHIPMENTS,
  ...RECENT_SHIPMENTS,
];

const queryMethods: QueryMethods = {
  me: userFactory({ preferredLanguage: 'en' }),
  shipmentsForConsignee: async ({ filter, page, first }) => {
    await delay();

    const filterOn = (list: Shipment[], filter?: QueryShipmentsForConsigneeArgs['filter']) => {
      return list.filter(
        ({ parcelId, box, updatedAt, state }) =>
          (filter?.state ? state && filter.state.includes(state) : true) &&
          (filter?.search ? parcelId?.toLocaleLowerCase().includes(filter.search) : true) &&
          (filter?.shipmentType === ('BOX' as ShipmentTypeFilter) ? box !== undefined : true) &&
          (filter?.fromDate ? isBefore(new Date(filter.fromDate), updatedAt) : true) &&
          (filter?.toDate ? isAfter(new Date(filter.toDate), updatedAt) : true),
      );
    };

    if (!page || !first) {
      throw new GraphQLError('page and first are required');
    }

    return slicedPaginatedData(filterOn(SHIPMENTS, filter), { page, first });
  },
  shipmentById: ({ id }) => SHIPMENTS.find((shipment) => shipment.externalUuid === id),
};

const mutationMethods: MutationMethods = {
  unlockBox: async ({ id }) => {
    await delay(1200);

    return {
      ...SHIPMENTS.find((shipment) => shipment.box?.id === id)?.box,
      locked: false,
    } as Box;
  },
  updateMe: async ({ id, input }) => {
    await delay(700);

    return userFactory({ id, ...input });
  },
  acceptPrivacyPolicy: async ({ id }) => {
    await delay(700);

    return userFactory({
      id,
      privacyPolicyAccepted: true,
      privacyPolicyAcceptedAt: new Date().toISOString(),
    });
  },
};

const root = {
  ...queryMethods,
  ...mutationMethods,
};

// const validationError = (field: string, errors: Record<string, string[]>) => {
//   return new GraphQLError(`Validation failed for the field [${field}].`, {
//     extensions: {
//       validation: errors,
//     },
//   });
// };
